import { useEffect } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { useShallow } from "zustand/shallow";
import { BookingState, RawPairInfo, useTradeStore } from "../../store/trade-store";

const optionsWss = {
  connectionTimeout: 5000,
  maxRetries: 1000,
};

// Enum-like object for trade event types to avoid magic strings
const TradeEvent = {
  BOOKING_INFO: "BOOKING-INFO",
};

// Define handlers for different event types
const eventHandlers = {
  [TradeEvent.BOOKING_INFO]: (payload: BookingState[]) => {
    useTradeStore.getState().setBookingData(payload?.length ? payload : []);
  },
};

export function useGetTrade() {
  const address = "0xad91fffae0e16bd97978a81d95aa489fbc334e6b";

  const resetOrderTradeData = useTradeStore(useShallow((state) => state.resetOrderTradeData));

  useEffect(() => {
    if (!address) {
      // No address, so don't attempt to connect and ensure data is reset.
      // Consider if resetOrderTradeData() is needed here or if it's handled by components
      // that depend on 'address' being present. For now, it's handled in the cleanup.
      return;
    }

    const url = `wss://api-trade-socket.holdstation.com/480/general/0xad91fffae0e16bd97978a81d95aa489fbc334e6b`;
    const socket = new ReconnectingWebSocket(url, [], optionsWss);

    console.debug(`Attempting to connect to WebSocket: ${url}`);

    socket.onopen = () => {
      resetOrderTradeData(); // Reset data on new connection
      console.debug("Successfully connected to socket trading server");
    };

    socket.onmessage = (event) => {
      try {
        const parsedData = JSON.parse(event.data);

        // Assuming parsedData is always an array as per original logic
        if (!Array.isArray(parsedData)) {
          console.warn("Received non-array data from WebSocket:", parsedData);
          return;
        }

        for (const message of parsedData) {
          const handler = eventHandlers[message.event];
          // MAP SANG WSS cũ sau ổn định thì viết lại
          if (handler) {
            if (message.event === TradeEvent.BOOKING_INFO) {
              const payload = message.payload.map((raw: RawPairInfo) => ({
                pair_index: Number(raw.pairIndex),
                symbol: raw.symbol,
                open_interest_long: raw.openInterestLong,
                open_interest_short: raw.openInterestShort,
                max_open_interest: raw.maxOpenInterest,
                funding_rate_long: raw.fundingRateLong,
                funding_rate_short: raw.fundingRateShort,
                rollover_rate: raw.rolloverRate,
                max_leverage: Number(raw.maxLeverage),
                spread: raw.spread,
                one_percent_depth_above: raw.onePercentDepthAbove,
                one_percent_depth_below: raw.onePercentDepthBelow,
              }));
              handler(payload);
            }
          } else {
            console.warn(`Received unhandled event type: ${message.event}`, message);
          }
        }
      } catch (err) {
        console.error("Error decoding trading message or processing event:", err, "Raw data:", event.data);
      }
    };

    socket.onerror = (err) => {
      // The ReconnectingWebSocket library handles retries.
      // This error is more for terminal failures or issues during connection attempts.
      console.error("Error on socket trading connection:", err);
    };

    // Cleanup function when the component unmounts or dependencies change
    return () => {
      if (socket) {
        console.debug("Closing WebSocket connection and resetting trade data.");
        resetOrderTradeData(); // Reset data when connection is closed
        socket.close();
      }
    };
  }, [address, resetOrderTradeData]);
}
